import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaHospital, FaMapMarkerAlt, FaPhone, FaEnvelope, FaIdCard, FaCalendarAlt, FaUpload, FaSave, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import { fetchClinicProfile, updateClinicProfile, clearError, clearSuccess } from '../../../redux/Slices/ClinicProfileSlice';
import LocationPicker from '../../../shared/components/LocationPicker';
import { useNavigate } from 'react-router-dom';

const ClinicProfileEdit = () => {
    const { setTitle } = useTitle();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { profileData, loading, updating, error, success } = useSelector((state) => state.clinicProfile);

    const [formData, setFormData] = useState({
        title_ar: '',
        title_en: '',
        description_ar: '',
        description_en: '',
        logo: null,
        cover: null,
        front_license: null,
        back_license: null,
        license_number: '',
        license_expiry: '',
        lat: '',
        lng: '',
        address_ar: '',
        address_en: ''
    });

    const [previews, setPreviews] = useState({
        logo: null,
        cover: null,
        front_license: null,
        back_license: null
    });

    const [shake, setShake] = useState(false);

    useEffect(() => {
        setTitle('تعديل الملف الشخصي للعيادة');
        dispatch(fetchClinicProfile());
    }, [setTitle, dispatch]);

    useEffect(() => {
        if (profileData) {
            // البيانات تأتي مباشرة من API response
            const clinicData = profileData?.result || profileData;
            
            setFormData({
                title_ar: clinicData.title || '',
                title_en: clinicData.title_en || '',
                description_ar: clinicData.description || '',
                description_en: clinicData.description_en || '',
                logo: null,
                cover: null,
                front_license: null,
                back_license: null,
                license_number: clinicData.license_number || '',
                license_expiry: clinicData.license_expiry || '',
                lat: clinicData.lat || '',
                lng: clinicData.lng || '',
                address_ar: clinicData.address || '',
                address_en: clinicData.address_en || ''
            });

            setPreviews({
                logo: clinicData.logo || null,
                cover: clinicData.cover || null,
                front_license: clinicData.front_license || null,
                back_license: clinicData.back_license || null
            });
        }
    }, [profileData]);

    useEffect(() => {
        if (error) {
            setShake(true);
            showErrorToast(error);
            dispatch(clearError());
            setTimeout(() => setShake(false), 500);
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (success) {
            showSuccessToast(success);
            dispatch(clearSuccess());
            navigate('/clinic/profile');
        }
    }, [success, dispatch, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const file = files[0];
        
        if (file) {
            setFormData(prev => ({
                ...prev,
                [name]: file
            }));

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviews(prev => ({
                    ...prev,
                    [name]: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const removeFile = (fieldName) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: null
        }));
        setPreviews(prev => ({
            ...prev,
            [fieldName]: null
        }));
    };

    const handleLocationChange = (lat, lng) => {
        setFormData(prev => ({
            ...prev,
            lat: lat.toString(),
            lng: lng.toString()
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const dataToSend = new FormData();
            
            // Add text fields
            dataToSend.append('title_ar', formData.title_ar);
            dataToSend.append('title_en', formData.title_en);
            dataToSend.append('description_ar', formData.description_ar);
            dataToSend.append('description_en', formData.description_en);
            dataToSend.append('license_number', formData.license_number);
            dataToSend.append('license_expiry', formData.license_expiry);
            dataToSend.append('lat', formData.lat);
            dataToSend.append('lng', formData.lng);
            dataToSend.append('address_ar', formData.address_ar);
            dataToSend.append('address_en', formData.address_en);

            // Add files if they exist
            if (formData.logo) dataToSend.append('logo', formData.logo);
            if (formData.cover) dataToSend.append('cover', formData.cover);
            if (formData.front_license) dataToSend.append('front_license', formData.front_license);
            if (formData.back_license) dataToSend.append('back_license', formData.back_license);

            await dispatch(updateClinicProfile(dataToSend)).unwrap();
        } catch (error) {
        }
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل بيانات العيادة...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <motion.button
                            className="btn btn-outline-secondary mx-3"
                            onClick={() => navigate('/clinic/profile')}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaArrowLeft className="mx-2" />
                            العودة
                        </motion.button>
                    </div>
                </div>

                {/* Profile Form */}
                <motion.div 
                    className={`card shadow-lg border-0 ${shake ? 'shake' : ''}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                {/* Basic Information */}
                                <div className="col-12">
                                    <h5 className="mb-4 text-primary">
                                        <FaHospital className="mx-2" />
                                        المعلومات الأساسية
                                    </h5>
                                </div>

                                {/* Clinic Name Arabic */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">اسم العيادة (عربي) *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="title_ar"
                                        value={formData.title_ar}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* Clinic Name English */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">اسم العيادة (إنجليزي) *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="title_en"
                                        value={formData.title_en}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* Description Arabic */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">الوصف (عربي)</label>
                                    <textarea
                                        className="form-control"
                                        name="description_ar"
                                        value={formData.description_ar}
                                        onChange={handleInputChange}
                                        rows="3"
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* Description English */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">الوصف (إنجليزي)</label>
                                    <textarea
                                        className="form-control"
                                        name="description_en"
                                        value={formData.description_en}
                                        onChange={handleInputChange}
                                        rows="3"
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* Images Section */}
                                <div className="col-12 mt-4">
                                    <h5 className="mb-4 text-primary">
                                        <FaUpload className="mx-2" />
                                        الصور والملفات
                                    </h5>
                                </div>

                                {/* Logo */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">لوجو العيادة</label>
                                    <div className="position-relative">
                                        <input
                                            type="file"
                                            className="form-control"
                                            name="logo"
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            style={{
                                                border: '2px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {previews.logo && (
                                            <div className="mt-2 position-relative d-inline-block">
                                                <img
                                                    src={previews.logo}
                                                    alt="Logo preview"
                                                    style={{
                                                        width: '100px',
                                                        height: '100px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        border: '2px solid #e9ecef'
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger position-absolute"
                                                    style={{ top: '-8px', right: '-8px' }}
                                                    onClick={() => removeFile('logo')}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Cover */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">صورة الغلاف</label>
                                    <div className="position-relative">
                                        <input
                                            type="file"
                                            className="form-control"
                                            name="cover"
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            style={{
                                                border: '2px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {previews.cover && (
                                            <div className="mt-2 position-relative d-inline-block">
                                                <img
                                                    src={previews.cover}
                                                    alt="Cover preview"
                                                    style={{
                                                        width: '100px',
                                                        height: '100px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        border: '2px solid #e9ecef'
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger position-absolute"
                                                    style={{ top: '-8px', right: '-8px' }}
                                                    onClick={() => removeFile('cover')}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* License Information */}
                                <div className="col-12 mt-4">
                                    <h5 className="mb-4 text-primary">
                                        <FaIdCard className="mx-2" />
                                        معلومات الترخيص
                                    </h5>
                                </div>

                                {/* License Number */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">رقم الترخيص</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="license_number"
                                        value={formData.license_number}
                                        onChange={handleInputChange}
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* License Expiry */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">تاريخ انتهاء الترخيص</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="license_expiry"
                                        value={formData.license_expiry}
                                        onChange={handleInputChange}
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* Front License */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">صورة الترخيص (الوجه الأمامي)</label>
                                    <div className="position-relative">
                                        <input
                                            type="file"
                                            className="form-control"
                                            name="front_license"
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            style={{
                                                border: '2px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {previews.front_license && (
                                            <div className="mt-2 position-relative d-inline-block">
                                                <img
                                                    src={previews.front_license}
                                                    alt="Front license preview"
                                                    style={{
                                                        width: '100px',
                                                        height: '100px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        border: '2px solid #e9ecef'
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger position-absolute"
                                                    style={{ top: '-8px', right: '-8px' }}
                                                    onClick={() => removeFile('front_license')}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Back License */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">صورة الترخيص (الوجه الخلفي)</label>
                                    <div className="position-relative">
                                        <input
                                            type="file"
                                            className="form-control"
                                            name="back_license"
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            style={{
                                                border: '2px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {previews.back_license && (
                                            <div className="mt-2 position-relative d-inline-block">
                                                <img
                                                    src={previews.back_license}
                                                    alt="Back license preview"
                                                    style={{
                                                        width: '100px',
                                                        height: '100px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        border: '2px solid #e9ecef'
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger position-absolute"
                                                    style={{ top: '-8px', right: '-8px' }}
                                                    onClick={() => removeFile('back_license')}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Location Section */}
                                <div className="col-12 mt-4">
                                    <h5 className="mb-4 text-primary">
                                        <FaMapMarkerAlt className="mx-2" />
                                        الموقع
                                    </h5>
                                </div>

                                {/* Address Arabic */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">العنوان (عربي)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="address_ar"
                                        value={formData.address_ar}
                                        onChange={handleInputChange}
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* Address English */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">العنوان (إنجليزي)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="address_en"
                                        value={formData.address_en}
                                        onChange={handleInputChange}
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                {/* Map */}
                                <div className="col-12 mb-4">
                                    <label className="form-label fw-semibold">اختر الموقع على الخريطة</label>
                                    <div style={{ height: '400px' }}>
                                        <LocationPicker
                                            lat={formData.lat}
                                            lng={formData.lng}
                                            onLocationChange={handleLocationChange}
                                            height="400px"
                                        />
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="col-12 text-center">
                                    <motion.button
                                        type="submit"
                                        className="btn btn-primary btn-lg px-5"
                                        disabled={updating}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            border: 'none',
                                            borderRadius: '12px',
                                            padding: '12px 32px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                                        }}
                                    >
                                        {updating ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm mx-2" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                جاري الحفظ...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mx-2" />
                                                حفظ التغييرات
                                            </>
                                        )}
                                    </motion.button>
                                </div>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </div>

            {/* CSS for shake animation */}
            <style jsx>{`
                .shake {
                    animation: shake 0.5s ease-in-out;
                }
                
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                }
            `}</style>
        </div>
    );
};

export default ClinicProfileEdit;
